import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckPermissions from "../../../authorization/authorization";
import { permissionsVariables } from "../../../services/Functions";

// ─── Seed data ────────────────────────────────────────────────────────────────
const SEED = [
  {
    title: "Initial Release",
    description: "eOps updates module has been added.",
    date: "2026-04-13",
    author: "Admin User",
  },
  {
    title: "Sidebar Enhancement",
    description: "Added eOps Updates entry under management section.",
    date: "2026-04-12",
    author: "System Team",
  },
  {
    title: "Permission Alignment",
    description: "Visibility aligned with Access Management permissions.",
    date: "2026-04-11",
    author: "Ops Support",
  },
  {
    title: "UI Polishing",
    description: "Improved table readability and spacing for long records.",
    date: "2026-04-10",
    author: "Design Team",
  },
  {
    title: "Routing Update",
    description: "Navigation route configured for eOps updates page.",
    date: "2026-04-09",
    author: "Admin User",
  },
  {
    title: "Dummy Dataset",
    description: "Added sample updates to verify layout and visual theme.",
    date: "2026-04-08",
    author: "System Team",
  },
  {
    title: "Release Notes",
    description: "Prepared changelog rows for upcoming production release.",
    date: "2026-04-07",
    author: "Release Desk",
  },
  {
    title: "Color Theme",
    description: "Applied header blue and alternating row striping.",
    date: "2026-04-06",
    author: "Design Team",
  },
  {
    title: "Data Model",
    description: "Standardized columns as title, description and date.",
    date: "2026-04-05",
    author: "Ops Support",
  },
  {
    title: "Audit Trail",
    description: "Added placeholders for future publication history.",
    date: "2026-04-04",
    author: "Admin User",
  },
  {
    title: "Performance",
    description: "Optimized rendering for larger list sizes.",
    date: "2026-04-03",
    author: "System Team",
  },
  {
    title: "Content Draft",
    description: "Prepared initial content blocks for admin announcements.",
    date: "2026-04-14",
    author: "Release Desk",
  },
];

// ─── Design tokens ────────────────────────────────────────────────────────────
const tk = {
  blue800: "#0C447C",
  blue600: "#185FA5",
  blue400: "#378ADD",
  blue200: "#85B7EB",
  blue100: "#B5D4F4",
  blue50: "#E6F1FB",
  green600: "#3B6D11",
  greenVivid: "#22C55E",
  greenVividSoft: "#DCFCE7",
  greenVividBorder: "#86EFAC",
  red600: "#A32D2D",
  red100: "#F7C1C1",
  red50: "#FCEBEB",
  border: "rgba(0,0,0,0.10)",
  borderHover: "rgba(0,0,0,0.18)",
  surface: "#ffffff",
  surfaceSub: "#F6F9FD",
  textPri: "#0F172A",
  textSec: "#475569",
  textHint: "#94A3B8",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function createRowId(prefix = "upd") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const seedWithIds = SEED.map((r, i) => ({ ...r, id: `seed-${i}` }));

function relDate(d) {
  const diff = Math.floor((new Date() - new Date(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "1d ago";
  return `${diff}d ago`;
}

function fullDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function todayStr() {
  const t = new Date();
  return new Date(t - t.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
}

function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function validate(v) {
  const e = {};
  if (!v.title.trim()) e.title = "Title is required.";
  else if (v.title.trim().length < 3) e.title = "Min 3 characters.";
  if (!v.description.trim()) e.description = "Description is required.";
  else if (v.description.trim().length < 10)
    e.description = "Min 10 characters.";
  if (!v.date) e.date = "Date is required.";
  else if (v.date > todayStr()) e.date = "Future date not allowed.";
  return e;
}

const EMPTY_FORM = { title: "", description: "", date: "" };

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ value, label, bg }) {
  return (
    <Box
      sx={{
        background: bg || tk.surfaceSub,
        borderRadius: "10px",
        px: 2,
        py: 1.75,
        flex: 1,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 600,
          color: tk.textPri,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: tk.textHint,
          mt: "3px",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ─── FilterChip ───────────────────────────────────────────────────────────────
function FilterChip({ label, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1.75,
        py: "5px",
        borderRadius: "20px",
        border: `0.5px solid ${active ? tk.blue100 : tk.border}`,
        background: active ? tk.blue50 : "transparent",
        color: active ? tk.blue800 : tk.textSec,
        fontSize: 12,
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        transition: "all 0.12s",
        "&:hover": { borderColor: tk.borderHover },
      }}
    >
      {label}
    </Box>
  );
}

// ─── ActionBtn ────────────────────────────────────────────────────────────────
function ActionBtn({ onClick, danger, title, children }) {
  return (
    <IconButton
      title={title}
      size="small"
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        borderRadius: "7px",
        border: `0.5px solid ${danger ? tk.red100 : tk.border}`,
        background: danger ? tk.red50 : "transparent",
        color: danger ? tk.red600 : tk.textSec,
        transition: "all 0.12s",
        "&:hover": {
          background: danger ? tk.red100 : tk.blue50,
          borderColor: danger ? tk.red600 : tk.blue200,
          color: danger ? tk.red600 : tk.blue600,
        },
      }}
    >
      {children}
    </IconButton>
  );
}

// ─── MetaRow ──────────────────────────────────────────────────────────────────
function MetaRow({ date, author }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CalendarTodayOutlinedIcon sx={{ fontSize: 11, color: tk.textHint }} />
        <Typography sx={{ fontSize: 11, color: tk.textHint }}>
          {fullDate(date)}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 3,
          height: 3,
          borderRadius: "50%",
          background: tk.textHint,
          opacity: 0.4,
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <PersonOutlineIcon sx={{ fontSize: 13, color: tk.textHint }} />
        <Typography sx={{ fontSize: 11, color: tk.textHint }}>
          {author}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── UpdateCard ───────────────────────────────────────────────────────────────
function UpdateCard({ row, onView, onEdit, onDelete, permissions }) {
  const isToday = relDate(row.date) === "Today";
  return (
    <Box sx={{ position: "relative", mb: 1.25 }}>
      <Box
        sx={{
          position: "absolute",
          left: -30,
          top: 16,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: tk.surface,
          border: `2px solid ${isToday ? tk.greenVivid : tk.blue400}`,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: isToday ? tk.greenVivid : tk.blue400,
          }}
        />
      </Box>

      <Box
        sx={{
          background: tk.surface,
          border: `0.5px solid ${isToday ? tk.greenVividBorder : tk.border}`,
          borderRadius: "12px",
          px: 2,
          py: 1.75,
          transition: "border-color 0.13s, background 0.13s",
          "&:hover": {
            borderColor: isToday ? tk.greenVivid : tk.borderHover,
            background: tk.surfaceSub,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.625,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.875,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: tk.blue600,
                lineHeight: 1.4,
              }}
            >
              {row.title}
            </Typography>
            {isToday && (
              <Box
                sx={{
                  background: tk.greenVividSoft,
                  color: tk.green600,
                  border: `0.5px solid ${tk.greenVividBorder}`,
                  borderRadius: "20px",
                  px: 1,
                  py: "2px",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                New
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.875,
              flexShrink: 0,
              mt: "1px",
            }}
          >
            <Typography
              sx={{ fontSize: 11, color: tk.textHint, whiteSpace: "nowrap" }}
            >
              {relDate(row.date)}
            </Typography>
            <Box sx={{ display: "flex", gap: "5px" }}>
              <ActionBtn title="View" onClick={() => onView(row)}>
                <VisibilityOutlinedIcon sx={{ fontSize: 12 }} />
              </ActionBtn>
              {CheckPermissions(
                permissions,
                permissionsVariables.accessManagement,
                "eOps Updates",
                "Update",
              ) && (
                <ActionBtn title="Edit" onClick={() => onEdit(row)}>
                  <EditOutlinedIcon sx={{ fontSize: 12 }} />
                </ActionBtn>
              )}
              {CheckPermissions(
                permissions,
                permissionsVariables.accessManagement,
                "eOps Updates",
                "Delete",
              ) && (
                <ActionBtn
                  title="Delete"
                  danger
                  onClick={() => onDelete(row.id)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                </ActionBtn>
              )}
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{ fontSize: 13, color: tk.textSec, lineHeight: 1.65, mb: 1.125 }}
        >
          {row.description}
        </Typography>
        <MetaRow date={row.date} author={row.author} />
      </Box>
    </Box>
  );
}

// ─── TimelineSection ──────────────────────────────────────────────────────────
function TimelineSection({ label, rows: sectionRows, ...cardProps }) {
  if (!sectionRows.length) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: tk.textHint,
          mb: 1.5,
        }}
      >
        {`${label} (${sectionRows.length})`}
      </Typography>
      <Box
        sx={{
          position: "relative",
          pl: "30px",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "8px",
            top: "6px",
            bottom: "6px",
            width: "1px",
            background: tk.border,
          },
        }}
      >
        {sectionRows.map((row) => (
          <UpdateCard
            key={`${row.id}-${row.title}-${row.date}`}
            row={row}
            {...cardProps}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── FormFields ───────────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 13,
    "& fieldset": { borderColor: tk.border, borderWidth: "0.5px" },
    "&:hover fieldset": { borderColor: tk.blue200 },
    "&.Mui-focused fieldset": { borderColor: tk.blue400, borderWidth: "1px" },
  },
};
const fLabelSx = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: tk.textSec,
  mb: 0.75,
};

function FormFields({ values, errors, onChange }) {
  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography sx={fLabelSx}>Title</Typography>
        <TextField
          fullWidth
          size="small"
          sx={inputSx}
          value={values.title}
          placeholder="Enter update title"
          onChange={(e) => onChange("title", e.target.value)}
        />
        {errors.title && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.title}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={fLabelSx}>Description</Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          size="small"
          sx={inputSx}
          value={values.description}
          placeholder="Enter description"
          onChange={(e) => onChange("description", e.target.value)}
        />
        {errors.description && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.description}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={fLabelSx}>Date</Typography>
        <TextField
          type="date"
          fullWidth
          size="small"
          sx={inputSx}
          value={values.date}
          inputProps={{ max: todayStr() }}
          onChange={(e) => onChange("date", e.target.value)}
        />
        {errors.date && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.date}
          </Typography>
        )}
      </Box>
    </>
  );
}

// ─── ModalShell ───────────────────────────────────────────────────────────────
const cancelBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontSize: 13,
  border: `0.5px solid ${tk.border}`,
  color: tk.textSec,
  "&:hover": { background: tk.surfaceSub },
};
const primaryBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontSize: 13,
  fontWeight: 500,
  background: tk.blue600,
  color: "#fff",
  boxShadow: "none",
  "&:hover": { background: tk.blue800, boxShadow: "none" },
};

function ModalShell({ open, onClose, title, children, footer }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          background: tk.blue600,
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
          py: 1.75,
          pr: 6,
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: "#fff",
            "&:hover": { background: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>{footer}</DialogActions>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const EopsUpdates = () => {
  const [rows, setRows] = useState(seedWithIds);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [formVals, setFormVals] = useState(EMPTY_FORM);
  const [formErrs, setFormErrs] = useState({});
  const [editVals, setEditVals] = useState(EMPTY_FORM);
  const [editErrs, setEditErrs] = useState({});
  const { permissions } = useSelector((s) => s.auth);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [rows],
  );

  const displayed = useMemo(() => {
    let list = sorted;
    if (search) {
      const q = search.trim();
      const pattern = new RegExp(escapeRegex(q), "i");
      list = list.filter(
        (r) =>
          pattern.test(String(r.title || "")) ||
          pattern.test(String(r.description || "")),
      );
    }
    if (filter === "week")
      list = list.filter(
        (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
      );
    if (filter === "earlier")
      list = list.filter(
        (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
      );
    return list;
  }, [sorted, search, filter]);

  const weekRows = displayed.filter(
    (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
  );
  const earlierRows = displayed.filter(
    (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
  );

  const handleAddClose = () => {
    setAddOpen(false);
    setFormVals(EMPTY_FORM);
    setFormErrs({});
  };
  const handleAddSubmit = () => {
    const errs = validate(formVals);
    setFormErrs(errs);
    if (Object.keys(errs).length) return;
    setRows((p) => [
      {
        id: createRowId(),
        title: formVals.title.trim(),
        description: formVals.description.trim(),
        date: formVals.date,
        author: "Admin User",
      },
      ...p,
    ]);
    setFilter("all");
    setSearch("");
    handleAddClose();
  };

  const handleEditOpen = (row) => {
    setEditRow(row);
    setEditVals({
      title: row.title,
      description: row.description,
      date: row.date,
    });
    setEditErrs({});
  };
  const handleEditClose = () => {
    setEditRow(null);
    setEditVals(EMPTY_FORM);
    setEditErrs({});
  };
  const handleEditSubmit = () => {
    const errs = validate(editVals);
    setEditErrs(errs);
    if (Object.keys(errs).length || !editRow) return;
    setRows((p) =>
      p.map((r) =>
        r.id === editRow.id
          ? {
              ...r,
              title: editVals.title.trim(),
              description: editVals.description.trim(),
              date: editVals.date,
              author: "Admin User",
            }
          : r,
      ),
    );
    handleEditClose();
  };

  const handleDelete = (id) => setRows((p) => p.filter((r) => r.id !== id));

  const cardProps = {
    onView: setViewRow,
    onEdit: handleEditOpen,
    onDelete: handleDelete,
    permissions,
  };

  return (
    <Box className="form-box">
      <Box className="application-table">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography
              sx={{ fontSize: 22, fontWeight: 600, color: tk.blue600 }}
            >
              eOps updates
            </Typography>
            <Box
              sx={{
                background: tk.blue50,
                color: tk.blue800,
                border: `0.5px solid ${tk.blue100}`,
                borderRadius: "20px",
                px: 1.25,
                py: "3px",
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              {rows.length} updates
            </Box>
          </Box>
          {CheckPermissions(
            permissions,
            permissionsVariables.accessManagement,
            "eOps Updates",
            "Create",
          ) && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={() => setAddOpen(true)}
              sx={{ ...primaryBtnSx, px: 2, py: 0.9 }}
            >
              Add update
            </Button>
          )}
        </Box>

        {/* Stats */}
        <Box sx={{ display: "flex", gap: 1.25, mb: 3.5 }}>
          <StatCard
  value={rows.length}
  label="Total updates"
  bg="linear-gradient(135deg, #e0e7ff, #c7d2fe)"   // soft indigo
/>

<StatCard
  value={
    rows.filter(
      (r) =>
        Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
    ).length
  }
  label="This week"
  bg="linear-gradient(135deg, #dcfce7, #bbf7d0)"   // soft green
/>

<StatCard
  value={
    rows.filter(
      (r) =>
        Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
    ).length
  }
  label="Earlier"
  bg="linear-gradient(135deg, #e0f2fe, #bae6fd)"   // soft blue
/>
        </Box>

        {/* Search + filters */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.75,
            flexWrap: "wrap",
          }}
        >
          <TextField
  size="small"
  placeholder="Search updates…"
  value={search}
  onChange={(e) => setSearch(e.target.value)}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchOutlinedIcon
          sx={{ fontSize: 16, color: tk.textHint }}
        />
      </InputAdornment>
    ),
  }}
  sx={{
    flex: 1,
    minWidth: 180,
    "& .MuiOutlinedInput-root": {
      borderRadius: "9px",
      fontSize: 13,
      height: 36,
      
      // === Added Background ===
      backgroundColor: "#f8fafc",        // Best recommended color
      
      "& fieldset": { 
        borderColor: tk.border, 
        borderWidth: "0.5px" 
      },
      "&:hover fieldset": { 
        borderColor: tk.borderHover 
      },
      "&.Mui-focused fieldset": {
        borderColor: tk.blue400,
        borderWidth: "1px",
      },
      
      // Hover effect for background
      "&:hover": {
        backgroundColor: "#f1f5f9",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
      },
    },
  }}
/>
          <Box sx={{ display: "flex", gap: "6px" }}>
            {[
              ["all", "All"],
              ["week", "This week"],
              ["earlier", "Earlier"],
            ].map(([val, lbl]) => (
              <FilterChip
                key={val}
                label={lbl}
                active={filter === val}
                onClick={() => setFilter(val)}
              />
            ))}
          </Box>
        </Box>

        {/* List */}
        {displayed.length === 0 ? (
          <Typography
            sx={{
              textAlign: "center",
              color: tk.textHint,
              fontSize: 13,
              py: 6,
            }}
          >
            No updates match your search
          </Typography>
        ) : (
          <>
            {filter !== "earlier" && (
              <TimelineSection
                label="This week"
                rows={weekRows}
                {...cardProps}
              />
            )}
            {filter !== "week" && (
              <TimelineSection
                label="Earlier"
                rows={earlierRows}
                {...cardProps}
              />
            )}
          </>
        )}

        {/* Add dialog */}
        <ModalShell
          open={addOpen}
          onClose={handleAddClose}
          title="Add eOps update"
          footer={
            <>
              <Button onClick={handleAddClose} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddSubmit}
                sx={primaryBtnSx}
              >
                Submit
              </Button>
            </>
          }
        >
          <FormFields
            values={formVals}
            errors={formErrs}
            onChange={(f, v) => {
              setFormVals((p) => ({ ...p, [f]: v }));
              setFormErrs((p) => ({ ...p, [f]: "" }));
            }}
          />
        </ModalShell>

        {/* View dialog */}
        <ModalShell
          open={Boolean(viewRow)}
          onClose={() => setViewRow(null)}
          title="View update"
          footer={
            <Button
              variant="contained"
              onClick={() => setViewRow(null)}
              sx={primaryBtnSx}
            >
              Close
            </Button>
          }
        >
          {viewRow && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              <Box>
                <Typography sx={{ ...fLabelSx, color: tk.textHint }}>
                  Title
                </Typography>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 600, color: tk.blue600 }}
                >
                  {viewRow.title}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ ...fLabelSx, color: tk.textHint }}>
                  Description
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: tk.textSec, lineHeight: 1.65 }}
                >
                  {viewRow.description}
                </Typography>
              </Box>
              <Box sx={{ borderTop: `0.5px solid ${tk.border}`, pt: 1.5 }}>
                <MetaRow date={viewRow.date} author={viewRow.author} />
              </Box>
            </Box>
          )}
        </ModalShell>

        {/* Edit dialog */}
        <ModalShell
          open={Boolean(editRow)}
          onClose={handleEditClose}
          title="Edit update"
          footer={
            <>
              <Button onClick={handleEditClose} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleEditSubmit}
                sx={primaryBtnSx}
              >
                Save changes
              </Button>
            </>
          }
        >
          <FormFields
            values={editVals}
            errors={editErrs}
            onChange={(f, v) => {
              setEditVals((p) => ({ ...p, [f]: v }));
              setEditErrs((p) => ({ ...p, [f]: "" }));
            }}
          />
        </ModalShell>
      </Box>
    </Box>
  );
};

export default EopsUpdates;
